import { deleteField } from 'firebase/firestore';

export const ASSIGNEE_KIND_HUMAN = 'human';
export const ASSIGNEE_KIND_AI = 'ai';

/**
 * @param {object} user
 * @returns {object}
 */
export function humanToAssigneeOption(user) {
  return {
    kind: ASSIGNEE_KIND_HUMAN,
    uid: user.uid,
    displayName: user.displayName || user.fullName || '',
    email: user.email || '',
    role: user.role || '',
  };
}

/**
 * @param {{ id: string, name?: string, roleTitle?: string, status?: string }} agent
 * @returns {object}
 */
export function agentToAssigneeOption(agent) {
  return {
    kind: ASSIGNEE_KIND_AI,
    agentId: agent.id,
    name: agent.name || 'AI Agent',
    roleTitle: agent.roleTitle || 'AI Worker',
    status: agent.status || 'active',
  };
}

/**
 * @param {object | null | undefined} a
 * @param {object | null | undefined} b
 * @returns {boolean}
 */
export function isSameAssigneeOption(a, b) {
  if (!a || !b) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === ASSIGNEE_KIND_AI) return a.agentId === b.agentId;
  return a.uid === b.uid;
}

/**
 * @param {object | null | undefined} option
 * @returns {string}
 */
export function assigneeOptionLabel(option) {
  if (!option) return '';
  if (option.kind === ASSIGNEE_KIND_AI) {
    const title = option.roleTitle ? ` — ${option.roleTitle}` : '';
    return `${option.name || 'AI Agent'}${title}`;
  }
  const base = option.displayName || option.email || option.uid || 'User';
  if (option.role === 'super_admin') return `${base} (Super Admin)`;
  return base;
}

/**
 * @param {object | null | undefined} option
 * @returns {string | null}
 */
export function assigneeOptionKey(option) {
  if (!option) return null;
  if (option.kind === ASSIGNEE_KIND_AI) return `ai:${option.agentId}`;
  if (option.uid) return `human:${option.uid}`;
  return null;
}

/**
 * @param {object | null | undefined} ticket
 * @returns {string | null}
 */
export function ticketAssigneeKey(ticket) {
  if (!ticket) return null;
  if (ticket.isAssignedToAi && ticket.assigneeAgentId) {
    return `ai:${ticket.assigneeAgentId}`;
  }
  if (ticket.assigneeUid) return `human:${ticket.assigneeUid}`;
  return null;
}

/**
 * @param {object[]} humans
 * @param {object[]} agents — full agent rows with `id`
 * @param {object | null} selectedOption
 * @returns {object[]}
 */
export function buildAssigneeAutocompleteOptions(humans, agents, selectedOption) {
  const humanOpts = humans.map(humanToAssigneeOption);
  const aiOpts = agents
    .filter((a) => !a.status || a.status === 'active')
    .map(agentToAssigneeOption);
  const all = [...humanOpts, ...aiOpts];
  if (selectedOption && !all.some((o) => isSameAssigneeOption(o, selectedOption))) {
    all.push(selectedOption);
  }
  return all;
}

/**
 * @param {object | null | undefined} ticket
 * @param {object[]} humanPool
 * @param {Record<string, object>} agentById
 * @returns {object | null}
 */
export function resolveAssigneeOptionFromTicket(ticket, humanPool, agentById) {
  if (!ticket) return null;
  if (ticket.isAssignedToAi && ticket.assigneeAgentId) {
    const agent = agentById[ticket.assigneeAgentId];
    if (agent) return agentToAssigneeOption(agent);
    return agentToAssigneeOption({
      id: ticket.assigneeAgentId,
      name: ticket.assigneeAgentName || 'AI Agent',
      roleTitle: ticket.assigneeAgentRole || 'AI Worker',
    });
  }
  if (ticket.assigneeUid) {
    const human = humanPool.find((u) => u.uid === ticket.assigneeUid);
    if (human) return humanToAssigneeOption(human);
    return humanToAssigneeOption({
      uid: ticket.assigneeUid,
      displayName: ticket.assigneeDisplayName || '',
      email: ticket.assigneeEmail || '',
      role: ticket.platformEscalation ? 'super_admin' : '',
    });
  }
  return null;
}

/**
 * @param {object | null | undefined} ticket
 * @param {Record<string, object>} userByUid
 * @param {Record<string, object>} agentById
 * @returns {string}
 */
export function ticketAssigneeDisplayLabel(ticket, userByUid, agentById) {
  if (!ticket) return '—';
  if (ticket.isAssignedToAi && ticket.assigneeAgentId) {
    const agent = agentById[ticket.assigneeAgentId];
    return agent?.name || ticket.assigneeAgentName || 'AI Worker';
  }
  if (!ticket.assigneeUid) return '—';
  const u = userByUid[ticket.assigneeUid];
  if (u) {
    const base = u.displayName || u.fullName || u.email || u.uid;
    return u.role === 'super_admin' ? `${base} (Super Admin)` : base;
  }
  if (ticket.platformEscalation) return 'Super Admin';
  return 'Unknown';
}

/**
 * @returns {boolean}
 */
export function ticketAssigneeIsAi(ticket) {
  return Boolean(ticket?.isAssignedToAi && ticket?.assigneeAgentId);
}

function buildAssignedUsers(reporterUid, assigneeUid) {
  return [...new Set([reporterUid, assigneeUid].filter(Boolean))];
}

/**
 * Firestore assignment fields for ticket create/update.
 * @param {object | null} formAssignee
 * @param {string} reporterUid
 * @param {{ forUpdate?: boolean }} [opts]
 * @returns {object}
 */
export function buildTicketAssignmentPayload(formAssignee, reporterUid, opts = {}) {
  const { forUpdate = false } = opts;
  const clearAgentId = forUpdate ? deleteField() : null;

  if (!formAssignee) {
    return {
      assigneeUid: null,
      assigneeAgentId: clearAgentId,
      isAssignedToAi: false,
      assignedUsers: buildAssignedUsers(reporterUid, null),
      platformEscalation: false,
    };
  }

  if (formAssignee.kind === ASSIGNEE_KIND_AI) {
    return {
      assigneeUid: null,
      assigneeAgentId: formAssignee.agentId,
      isAssignedToAi: true,
      assignedUsers: buildAssignedUsers(reporterUid, null),
      platformEscalation: false,
    };
  }

  const assigneeUid = formAssignee.uid || null;
  return {
    assigneeUid,
    assigneeAgentId: clearAgentId,
    isAssignedToAi: false,
    assignedUsers: buildAssignedUsers(reporterUid, assigneeUid),
    platformEscalation: Boolean(assigneeUid && formAssignee.role === 'super_admin'),
  };
}

/**
 * When status is terminal, drop AI ownership on the ticket (roster agent
 * status is unchanged). Used by client writers; CF trigger is the safety net.
 * @param {string} status
 * @param {{ forUpdate?: boolean }} [opts]
 * @returns {object}
 */
export function buildClearAiAssignmentIfTerminal(status, opts = {}) {
  const { forUpdate = true } = opts;
  if (status !== 'resolved' && status !== 'closed') return {};
  return {
    isAssignedToAi: false,
    assigneeAgentId: forUpdate ? deleteField() : null,
  };
}
